import { cx } from '@linaria/core';
import cc from 'classcat';
import { useRef } from 'react';
import { CopyIcon, MoveIcon } from '@i-ark/components';
import { useActiveEventHandler } from '@i-ark/hooks';
import { isTextItem } from '@i-ark/models';
import { TrashIcon } from '@ui/kit/icons';
import '@utils/component-framework';
import { useDeleteCloneEntryMutation } from '../../../state/mutations/entries/clone.delete.js';
import '@tanstack/react-query';
import { iArkStore } from '../../../state/store.js';
import { useShortcut } from '../../hook.use-shortcut.js';
import { useUserOwnsEntry } from '../../hook.user-owns-entry.js';
import { itemSupportsDrag } from '../utils/itemSupportsDrag.js';
import { rotation, buttonCls, deleteHandleCls, dragHandleCls, overlayCls } from './After.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

/* eslint-disable react-hooks/rules-of-hooks */
const After = ({
  pageId,
  item,
  isSelected,
  isCloned,
  onDragStart
}) => {
  if (item.kind === "line") {
    return null;
  }
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const deleteRef = useRef(null);
  const deleteMutation = useDeleteCloneEntryMutation(entryKey);
  const userOwnsEntry = useUserOwnsEntry(entryKey);

  // we need to disable drag'n'drop functionality when interacting directly with
  // the delete-button
  useActiveEventHandler(deleteRef, "mousedown", evt => {
    evt.stopPropagation();
  });
  useActiveEventHandler(deleteRef, "touchstart", evt => {
    evt.stopPropagation();
  });
  const handleDeleteClick = () => {
    deleteMutation({
      pageId,
      cloneId: item.id
    });
  };
  useShortcut("Backspace", evt => {
    // Ensure that we do not accidentally delete anything when deleting
    // inside an input.
    if (evt.target instanceof HTMLInputElement || evt.target instanceof HTMLTextAreaElement) {
      return;
    }
    if (userOwnsEntry && isCloned && isSelected) {
      handleDeleteClick();
    }
  });
  useShortcut("Delete", evt => {
    // Ensure that we do not accidentally delete anything when deleting
    // inside an input.
    if (evt.target instanceof HTMLInputElement || evt.target instanceof HTMLTextAreaElement) {
      return;
    }
    if (userOwnsEntry && isCloned && isSelected) {
      handleDeleteClick();
    }
  });
  if (!userOwnsEntry || !itemSupportsDrag(item) || !item.settings.cloneable && !item.settings.movable) {
    return null;
  }
  const isTransparent = isTextItem(item) ? item.style.background.color === "transparent" || typeof item.style.background.opacity === "number" && !item.style.background.opacity : false;
  const handleKeyDown = evt => {
    if ((evt.key === " " || evt.key === "Enter") && !document.body.classList.contains("isDragging")) {
      onDragStart?.(evt, item);
    }
  };
  return /*#__PURE__*/jsxs("div", {
    className: cx(overlayCls, item.settings.movable && "moveable", item.settings.cloneable && "cloneable", isCloned && "isCloned", isTransparent && "isTransparent"),
    "data-type": item.kind,
    children: [isCloned && /*#__PURE__*/jsx("button", {
      ref: deleteRef,
      className: cc(["afterButton", buttonCls, deleteHandleCls]),
      onClick: handleDeleteClick,
      style: "angleInRadian" in item ? rotation.__override(`-${item.angleInRadian}rad`) : undefined,
      children: /*#__PURE__*/jsx(TrashIcon, {
        height: 11
      })
    }), !isCloned && item.settings.cloneable && /*#__PURE__*/jsx("button", {
      onMouseDown: evt => onDragStart?.(evt, item),
      onTouchStart: evt => onDragStart?.(evt, item),
      onKeyDown: handleKeyDown,
      className: cc(["afterButton", buttonCls, dragHandleCls]),
      style: "angleInRadian" in item ? rotation.__override(`-${item.angleInRadian}rad`) : undefined,
      children: /*#__PURE__*/jsx(CopyIcon, {
        size: 11
      })
    }), (item.settings.movable || isCloned && item.kind === "input") && /*#__PURE__*/jsx("button", {
      onMouseDown: evt => onDragStart?.(evt, item),
      onTouchStart: evt => onDragStart?.(evt, item),
      onKeyDown: handleKeyDown,
      className: cc(["afterButton", buttonCls, dragHandleCls]),
      style: "angleInRadian" in item ? rotation.__override(`-${item.angleInRadian}rad`) : undefined,
      children: /*#__PURE__*/jsx(MoveIcon, {
        size: 13
      })
    })]
  });
};

export { After };
//# sourceMappingURL=After.js.map
